import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {
    NavTarget,
    ScaffoldRouteContext,
    ViewType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {configPageHref} from "~/workspaceConfig/configPageRoutes";
import {resolvePageIdFromNavTarget} from "~/workspaceConfig/scaffoldNavigation";

export {resolvePageIdFromNavTarget};

export function resolveExternalHref(args: {target: NavTarget; workspaceId: Uuid}): string | null {
    const {target, workspaceId} = args;
    if (target.type === "absolutePath") {
        return target.pathTemplate.replaceAll("$workspaceId", workspaceId);
    }
    return null;
}

export function isConfigScaffoldNavTarget(target: NavTarget): boolean {
    return target.type === "page";
}

export function resolveSearchFromNavTarget(args: {
    target: NavTarget;
    view?: ViewType | string;
    statusId?: Uuid;
}): {view?: ViewType | string; statusId?: Uuid} {
    const search: {view?: ViewType | string; statusId?: Uuid} = {};
    if (args.view !== undefined) {
        search.view = args.view;
    }
    if (args.statusId !== undefined) {
        search.statusId = args.statusId;
    }
    return search;
}

export function buildConfigScaffoldHref(args: {
    workspaceId: Uuid;
    pageId: Uuid;
    view?: ViewType | string;
    statusId?: Uuid;
}): string {
    const basePath = configPageHref(args.workspaceId, args.pageId);
    const params = new URLSearchParams();
    if (args.view !== undefined) {
        params.set("view", String(args.view));
    }
    if (args.statusId !== undefined) {
        params.set("statusId", String(args.statusId));
    }
    const query = params.toString();
    return query.length > 0 ? `${basePath}?${query}` : basePath;
}

export function matchesNavTargetRoute(args: {
    target: NavTarget;
    route: ScaffoldRouteContext;
    view?: ViewType | string;
    statusId?: Uuid;
}): boolean {
    const pageId = resolvePageIdFromNavTarget(args.target);
    if (pageId === null || pageId !== args.route.pageKey) {
        return false;
    }
    if (args.statusId !== undefined) {
        return args.route.statusId === args.statusId;
    }
    if (args.view !== undefined) {
        return args.route.view === args.view;
    }
    return true;
}

export function isPartiallyActiveNavTarget(args: {
    target: NavTarget;
    route: ScaffoldRouteContext;
    nestedStatusIds: Array<Uuid>;
    nestedViews: Array<string>;
    nestedPageIds: Array<Uuid>;
}): boolean {
    const pageId = resolvePageIdFromNavTarget(args.target);
    if (pageId !== null && pageId === args.route.pageKey) {
        if (args.route.statusId !== undefined && args.nestedStatusIds.includes(args.route.statusId)) {
            return true;
        }
        if (args.route.view !== undefined && args.nestedViews.includes(String(args.route.view))) {
            return true;
        }
        return args.nestedStatusIds.length === 0 && args.nestedViews.length === 0 && args.nestedPageIds.length === 0;
    }
    return args.nestedPageIds.some((nestedPageId) => nestedPageId === args.route.pageKey);
}
